import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { copy } from '../../config/copy';
import { Modal } from '../albums/Modal';
import { rooms } from '../rooms/api';
import { useLiveHosts } from '../rooms/useLiveHosts';

/**
 * What an earned boost is, and how to get one.
 *
 * ## Why it needs explaining at all
 *
 * Every other number on this site is a count of something obvious - plays,
 * saves, followers. This one is a currency with a rule behind it, and nobody
 * guesses "get hosts to play tracks you send them" from a progress bar. Until
 * this existed the only explanation lived in a sentence on the dashboard card,
 * which somebody has to already be looking at.
 *
 * ## The rules come from the server
 *
 * Every figure quoted here - what a play is worth, the threshold, the cooldown,
 * the daily ceiling - is in the response. The same argument as
 * `Weighting::legend()`: copy that explains a rule must not be able to describe
 * one the server does not apply, and hardcoding "10 points" here would survive
 * exactly until somebody tuned the constant.
 *
 * ## It ends by pointing at a room
 *
 * The whole thing is unearnable without a live host, so the last thing it says
 * is whether there is one - and if there is, it stops explaining and gets out
 * of the way.
 */
export function BoostsDialog({ onClose }: { onClose: () => void }) {
  const { data } = useQuery({ queryKey: ['my-boosts'], queryFn: () => rooms.boosts() });
  const { count: liveNow } = useLiveHosts();

  const name = copy['noun.boost.earned'];

  if (!data) {
    return (
      <Modal title={copy['noun.boost.earned.plural']} onClose={onClose}>
        <p className="py-6 text-center text-sm text-soft">Looking…</p>
      </Modal>
    );
  }

  const pct = Math.min(100, Math.round((data.toward_next / data.threshold) * 100));
  const playsPerBoost = Math.round(data.threshold / data.rules.per_play);

  return (
    <Modal title={copy['noun.boost.earned.plural']} onClose={onClose}>
      <div className="flex flex-col gap-6" data-boosts-dialog>
        {/* ---------------------------------------------------------- */}
        {/* Where you are                                               */}
        {/* ---------------------------------------------------------- */}
        <div className="flex flex-col gap-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-3xl font-semibold tracking-tight text-ink">
              {data.available > 0 ? (
                <>
                  {data.available}{' '}
                  <span className="text-base font-normal text-soft">
                    ready to spend
                  </span>
                </>
              ) : (
                <>
                  {data.toward_next}
                  <span className="text-lg font-normal text-faint"> / {data.threshold}</span>
                </>
              )}
            </span>
            {data.available > 0 && (
              <span className="meta-caps text-faint">
                hold up to {data.max_held}
              </span>
            )}
          </div>

          <div
            className="h-1.5 overflow-hidden rounded-[var(--radius-pill)] bg-[#22224a]"
            role="progressbar"
            aria-valuenow={data.toward_next}
            aria-valuemin={0}
            aria-valuemax={data.threshold}
            aria-label={`Progress toward your next ${name}`}
          >
            <div
              className="h-full rounded-[var(--radius-pill)] bg-fresh transition-[width] duration-500"
              style={{ width: `${data.available > 0 ? 100 : pct}%` }}
            />
          </div>
        </div>

        {/* ---------------------------------------------------------- */}
        {/* What it is                                                  */}
        {/* ---------------------------------------------------------- */}
        <p className="text-sm leading-relaxed text-soft">
          A {name} is the one boost nobody can buy. Spend it when you submit a track to a live
          host and it goes into their queue{' '}
          <span className="text-ink">above every paid boost</span> — only the host's own pick
          outranks it. They never expire.
        </p>

        {/* ---------------------------------------------------------- */}
        {/* How to earn one                                             */}
        {/* ---------------------------------------------------------- */}
        <div className="flex flex-col gap-3">
          <span className="meta-caps text-faint">How you earn them</span>

          <p className="text-sm leading-relaxed text-soft">
            Send <span className="text-ink">other artists'</span> tracks to hosts who are on air.
            Every one a host plays through earns you {data.rules.per_play} points, and{' '}
            {data.threshold} points is a {name} — about {playsPerBoost} good calls.
          </p>

          {/* The exclusions, said plainly. Somebody who works out a rule by
              watching a counter not move learns to distrust the counter. */}
          <ul className="flex flex-col gap-2 border-l border-line pl-4">
            <Rule>Your own tracks never count. This rewards finding, not posting.</Rule>
            <Rule>
              If the host skips it early, it does not count — they have to play it through.
            </Rule>
            <Rule>
              One artist pays out once every {data.rules.artist_cooldown_days} days, so a single
              friend's catalogue is not a shortcut.
            </Rule>
            <Rule>
              Up to {data.rules.daily_cap / data.rules.per_play} plays a day count, so nobody
              earns one in an afternoon.
            </Rule>
          </ul>
        </div>

        {/* ---------------------------------------------------------- */}
        {/* What you already earned                                     */}
        {/* ---------------------------------------------------------- */}
        {data.recent.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="meta-caps text-faint">Recently</span>
            <ul className="flex flex-col gap-1.5">
              {data.recent.map((credit, index) => (
                <li
                  key={`${credit.at}-${index}`}
                  className="flex items-baseline justify-between gap-3 font-mono text-[11px]"
                >
                  <span className="min-w-0 truncate text-faint">
                    {credit.host ? (
                      <>
                        <Link
                          to={`/${credit.host_username}`}
                          onClick={onClose}
                          className="text-soft hover:text-ink hover:underline"
                        >
                          {credit.host}
                        </Link>{' '}
                        played {credit.track ?? 'a track you sent'}
                      </>
                    ) : (
                      (credit.track ?? 'a track you sent')
                    )}
                  </span>
                  <span className="shrink-0 text-fresh">+{credit.points}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ---------------------------------------------------------- */}
        {/* Go and do it                                                */}
        {/* ---------------------------------------------------------- */}
        <div className="border-t border-line pt-4">
          {liveNow > 0 ? (
            <p className="text-sm text-soft">
              <span className="text-fresh">
                {liveNow === 1 ? '1 host is' : `${liveNow} hosts are`} on air right now.
              </span>{' '}
              Open the live count in the header to find them.
            </p>
          ) : (
            <p className="text-sm text-soft">
              Nobody is on air at the moment. When somebody goes live they appear in the header,
              and their profile is where you submit.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Rule({ children }: { children: React.ReactNode }) {
  return <li className="text-[13px] leading-relaxed text-soft">{children}</li>;
}
